import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import * as authApi from "@vaulzo/core/api/auth";
import { onUnauthorized } from "@vaulzo/core/http";
import { getToken, setToken as storeToken, clearSession } from "@vaulzo/core/session";

const AuthContext = createContext(null);

/**
 * Owns the session for the web app: login/register/logout and the global
 * 401 → logout hook. Token persistence and the Authorization header live
 * in @vaulzo/core (session.js / http.js), shared with the mobile app.
 * Must render inside BrowserRouter (it navigates on logout).
 */
export function AuthProvider({ children }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [token, setToken] = useState(getToken());

  const logout = useCallback(() => {
    clearSession(); // token + cached FX, so the next account starts fresh
    setToken(null);
    queryClient.clear();
    toast.info("Logged out");
    navigate("/");
  }, [navigate, queryClient]);

  // Expired/invalid sessions: any authenticated call answering 401 logs
  // the user out (auth endpoints are exempt inside onUnauthorized).
  useEffect(() => onUnauthorized(logout), [logout]);

  // Both throw on failure so AuthPage can show the error inline next to
  // the field; navigation after success is AuthPage's job.
  const login = useCallback(async ({ email, password }) => {
    const data = await authApi.login(email, password);
    storeToken(data.access_token);
    setToken(data.access_token);
    toast.success("Logged in!");
  }, []);

  const register = useCallback(async ({ email, password }) => {
    const data = await authApi.register(email, password);
    storeToken(data.access_token);
    setToken(data.access_token);
    toast.success(data.message || "Account created!");
  }, []);

  return (
    <AuthContext.Provider value={{ token, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
